import { createTheme, alpha } from '@mui/material/styles';

// MQBIT design tokens — mirrors the `mqbit` palette in tailwind.config.js so MUI
// components and Tailwind markup render as one visual system.
export const mqbit = {
  50: '#f5f3ff',
  100: '#ede9fe',
  200: '#ddd6fe',
  300: '#c4b5fd',
  400: '#a78bfa',
  500: '#8b5cf6',
  600: '#7c3aed',
  700: '#6d28d9',
  800: '#5b21b6',
  900: '#4c1d95',
  950: '#2e1065',
};

const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

const theme = createTheme({
  palette: {
    primary: { light: mqbit[400], main: mqbit[600], dark: mqbit[700], contrastText: '#fff' },
    secondary: { light: '#7dd3fc', main: '#0ea5e9', dark: '#0284c7', contrastText: '#fff' },
    success: { light: '#6ee7b7', main: '#059669', dark: '#047857', contrastText: '#fff' },
    warning: { light: '#fcd34d', main: '#d97706', dark: '#b45309', contrastText: '#fff' },
    error: { light: '#fda4af', main: '#e11d48', dark: '#be123c', contrastText: '#fff' },
    info: { light: mqbit[300], main: mqbit[500], dark: mqbit[700], contrastText: '#fff' },
    background: { default: slate[50], paper: '#ffffff' },
    text: { primary: slate[800], secondary: slate[500], disabled: slate[400] },
    divider: slate[200],
    grey: slate as any,
    action: {
      hover: slate[50],
      selected: mqbit[50],
    },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: { fontWeight: 800, color: slate[900] },
    h2: { fontWeight: 800, color: slate[900] },
    h3: { fontWeight: 800, color: slate[900], fontSize: '2rem' },
    h4: { fontWeight: 700, color: slate[900], fontSize: '1.5rem', lineHeight: 1.3 },
    h5: { fontWeight: 700, color: slate[900], fontSize: '1.25rem', lineHeight: 1.35 },
    h6: { fontWeight: 700, color: slate[900], fontSize: '1rem', lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, color: slate[700] },
    subtitle2: { fontWeight: 600, color: slate[700], fontSize: '0.8125rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', color: slate[500] },
    overline: { fontWeight: 600, letterSpacing: '0.08em', color: slate[400] },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.06)',
    '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
    '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
    '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
    '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
    '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.08)',
    ...Array(16).fill('0 25px 50px -12px rgb(0 0 0 / 0.2)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: slate[50] } },
    },

    // ── Surfaces ──────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
        elevation0: { border: `1px solid ${slate[200]}`, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
        elevation1: { border: `1px solid ${slate[200]}`, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
        elevation2: { border: `1px solid ${slate[200]}`, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.08)' },
        elevation3: { border: `1px solid ${slate[200]}`, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: 24, border: `1px solid ${slate[200]}`, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '20px 24px 0' },
        title: { fontSize: '1rem', fontWeight: 700, color: slate[900] },
        subheader: { fontSize: '0.75rem', color: slate[400] },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: slate[100] } },
    },

    // ── Buttons ───────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 12, padding: '8px 16px', fontWeight: 600, transition: 'all .15s ease' },
        sizeSmall: { padding: '5px 12px', fontSize: '0.75rem', borderRadius: 10 },
        sizeLarge: { padding: '11px 22px', fontSize: '0.875rem' },
        contained: ({ ownerState, theme: t }) => {
          const key = (ownerState.color && ownerState.color !== 'inherit' ? ownerState.color : 'primary') as
            'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
          const main = t.palette[key]?.main ?? mqbit[600];
          return {
            fontWeight: 700,
            boxShadow: `0 4px 6px -1px ${alpha(main, 0.2)}, 0 2px 4px -2px ${alpha(main, 0.2)}`,
            '&:hover': { boxShadow: `0 10px 15px -3px ${alpha(main, 0.3)}` },
          };
        },
        outlined: { borderColor: slate[200], '&:hover': { borderColor: mqbit[300], backgroundColor: mqbit[50] } },
        outlinedPrimary: { borderColor: mqbit[200], color: mqbit[700], backgroundColor: mqbit[50],
          '&:hover': { backgroundColor: mqbit[100], borderColor: mqbit[300] } },
        text: { '&:hover': { backgroundColor: mqbit[50] } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, '&:hover': { backgroundColor: slate[100] } },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderColor: slate[200],
          '&.Mui-selected': { backgroundColor: mqbit[50], color: mqbit[700], borderColor: mqbit[200] } },
      },
    },
    MuiFab: {
      styleOverrides: { root: { boxShadow: `0 10px 15px -3px ${alpha(mqbit[600], 0.3)}` } },
    },

    // ── Form controls ────────────────────────────────────────
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.8125rem', fontWeight: 500, color: slate[500], '&.Mui-focused': { color: mqbit[600] } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: slate[50],
          fontSize: '0.8125rem',
          transition: 'box-shadow .15s ease, background-color .15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: slate[200] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: slate[300] },
          '&.Mui-focused': { backgroundColor: '#fff', boxShadow: `0 0 0 3px ${alpha(mqbit[500], 0.18)}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mqbit[500], borderWidth: 1 },
          '&.Mui-disabled': { backgroundColor: slate[100] },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.7rem', marginLeft: 4 } },
    },
    MuiFormLabel: {
      styleOverrides: { root: { fontSize: '0.8125rem', fontWeight: 600, color: slate[700] } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: slate[300] } },
    },
    MuiRadio: {
      styleOverrides: { root: { color: slate[300] } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1, backgroundColor: mqbit[500] } },
        track: { backgroundColor: slate[300], opacity: 1 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${slate[100]}`, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' },
        option: { fontSize: '0.8125rem', '&[aria-selected="true"]': { backgroundColor: `${mqbit[50]} !important` } },
      },
    },

    // ── Menus / popovers ─────────────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${slate[100]}`, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.08)', marginTop: 4 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem', borderRadius: 8, color: slate[700],
          '&:hover': { backgroundColor: mqbit[50], color: mqbit[700] },
          '&.Mui-selected': { backgroundColor: mqbit[50], color: mqbit[700], fontWeight: 600 },
          '&.Mui-selected:hover': { backgroundColor: mqbit[100] },
        },
      },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: slate[900], fontSize: '0.7rem', fontWeight: 600, borderRadius: 8, padding: '6px 10px' },
        arrow: { color: slate[900] },
      },
    },

    // ── Tables ───────────────────────────────────────────────
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: 16 } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: slate[50],
          '& .MuiTableCell-head': {
            fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
            color: slate[500], borderBottom: `1px solid ${slate[200]}`, whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${slate[100]}`, fontSize: '0.8125rem', color: slate[600], padding: '12px 16px' },
        // A real color, not 'inherit' — a sticky-positioned head cell (Table stickyHeader) paints
        // itself independently while scrolling, so it needs its own opaque background or the
        // body rows visually show through/overlap it. Matches MuiTableHead's root background
        // below so plain (non-sticky) tables look exactly the same as before.
        head: { fontWeight: 600, color: slate[500], backgroundColor: slate[50] },
        sizeSmall: { padding: '9px 14px' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color .15s ease',
          '&.MuiTableRow-hover:hover': { backgroundColor: slate[50] },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${slate[100]}`, color: slate[500] },
        selectLabel: { fontSize: '0.75rem' },
        displayedRows: { fontSize: '0.75rem' },
      },
    },

    // ── Status & feedback ────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 9999, fontWeight: 700, fontSize: '0.7rem' },
        sizeSmall: { height: 22 },
        filled: ({ ownerState }) => {
          const map: Record<string, [string, string]> = {
            primary: [mqbit[100], mqbit[700]],
            secondary: ['#e0f2fe', '#0369a1'],
            success: ['#d1fae5', '#065f46'],
            warning: ['#fef3c7', '#92400e'],
            error: ['#ffe4e6', '#9f1239'],
            info: [mqbit[50], mqbit[700]],
            default: [slate[100], slate[600]],
          };
          const [bg, fg] = map[ownerState.color ?? 'default'] ?? map.default;
          return { backgroundColor: bg, color: fg, '& .MuiChip-icon': { color: fg } };
        },
        outlined: { borderColor: slate[200] },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 16, fontSize: '0.8125rem', alignItems: 'center' },
        standardSuccess: { backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' },
        standardError: { backgroundColor: '#fff1f2', color: '#9f1239', border: '1px solid #fecdd3' },
        standardWarning: { backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
        standardInfo: { backgroundColor: mqbit[50], color: mqbit[800], border: `1px solid ${mqbit[200]}` },
        filledSuccess: { backgroundColor: '#059669' },
        filledInfo: { backgroundColor: mqbit[700] },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: { root: { borderRadius: 16, fontWeight: 700, fontSize: '0.75rem' } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 8, borderRadius: 9999, backgroundColor: slate[100] },
        bar: { borderRadius: 9999 },
      },
    },
    MuiBadge: {
      styleOverrides: { badge: { fontWeight: 700 } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: mqbit[100], color: mqbit[700], fontWeight: 700, fontSize: '0.875rem' },
        rounded: { borderRadius: 12 },
      },
    },

    // ── Dialogs ──────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 24, border: `1px solid ${slate[200]}`, boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)' },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgb(2 6 23 / 0.6)', backdropFilter: 'blur(4px)' } },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 700, color: slate[900], backgroundColor: mqbit[50],
          borderBottom: `1px solid ${slate[100]}`, padding: '16px 24px', marginBottom: 8 },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '16px 24px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '14px 24px', borderTop: `1px solid ${slate[100]}`, gap: 8 } },
    },

    // ── Navigation ───────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${slate[200]}` },
        indicator: { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: mqbit[600] },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 44, color: slate[500],
          '&.Mui-selected': { color: mqbit[700] } },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${slate[200]}`, borderRadius: 16, '&:before': { display: 'none' }, marginBottom: 8, overflow: 'hidden' },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: { root: { fontWeight: 600, '&.Mui-expanded': { backgroundColor: slate[50] } } },
    },
    MuiStepIcon: {
      styleOverrides: { root: { '&.Mui-active, &.Mui-completed': { color: mqbit[600] } } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 12, '&.Mui-selected': { backgroundColor: mqbit[50], color: mqbit[700] } },
      },
    },
    MuiLink: {
      styleOverrides: { root: { color: mqbit[600], fontWeight: 600 } },
    },
  },
});

export default theme;
